import React, { useState } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { motion } from "framer-motion";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";

// Wrapper with RTL & Custom Font
const PageWrapper = styled.div`
  ${tw`max-w-6xl mx-auto py-10`}
  direction: rtl;
  text-align: right;
  font-family: "CustomFont", sans-serif;
`;

// Main Department Styles
const MainDeptWrapper = styled.div`
  ${tw`border border-gray-400 rounded-lg p-8 mb-8 bg-white text-center`}
`;
const MainDeptTitle = styled.div`
  ${tw`font-bold text-4xl mb-5`} 
  color: var(--primary-color);
`;
const MainDeptResponsibles = styled.div`
  ${tw`text-2xl leading-relaxed`}
  span {
    ${tw`font-bold`}
    color: var(--primary-color);
  }
`;

// Accordion Styles
const AccordionContainer = tw.div`w-full mb-8 bg-white border border-gray-400 rounded-lg`;
const AccordionHeader = tw.div`flex justify-between items-center cursor-pointer px-6 py-5 border-b border-gray-300`;
const AccordionTitle = styled.span`
  ${tw`text-3xl font-bold`}
  color: #264b3e;
`;
const AccordionIcon = motion(styled.span`${tw`ml-2 transition-transform duration-300`}`);
const AccordionContent = motion(styled.div`${tw`overflow-hidden p-6`}`);

// Boxes & Text Styles
const BoxesRow = tw.div`flex flex-col md:flex-row gap-6 mb-6`;
const Box = styled.div`
  ${tw`flex-1 border border-gray-400 rounded-lg p-5 mb-4 md:mb-0 bg-gray-100`}
  font-size: 1.375rem; /* ~text-2xl */
  text-align: right;
`;
const DescriptionBox = styled.div`
  ${tw`border border-gray-400 rounded-lg p-6 text-gray-700 bg-white`}
  font-size: 1.375rem; /* ~text-2xl */
  text-align: right;
`;
const Responsibility = styled.div`
  ${tw`flex items-center justify-between mb-3`}
  font-size: 1.375rem; /* ~text-2xl */
  direction: rtl;
`;


const Role = styled.span`
  direction: rtl;       /* RTL so colon appears after text correctly */
  text-align: right;
  font-weight: bold;
  color: var(--primary-color);

  &::after {
    content: " :";       /* colon after the role */
  }
`;
const Name = styled.span`
  direction: rtl;
  text-align: right;
`;

// Animation Variants
const variants = {
  open: { opacity: 1, height: "auto", marginTop: "1rem", pointerEvents: "auto" },
  collapsed: { opacity: 0, height: 0, marginTop: "0rem", pointerEvents: "none" }
};
const iconVariants = {
  collapsed: { rotate: 0 },
  open: { rotate: -180 }
};

// ✅ Reusable Responsibility Group
const ResponsibilitiesGroup = ({ data }) => (
  <Box>
    {data.map((item, idx) => (
      <Responsibility key={idx}>
        <Role>{item.role}</Role>&nbsp;
        <Name>{item.name}</Name>
      </Responsibility>
    ))}
  </Box>
);

// Accordion Component
function SubDepartmentAccordion({ title, responsibilities1 = [], responsibilities2 = [], description }) {
  const [open, setOpen] = useState(false);

  return (
    <AccordionContainer>
      <AccordionHeader onClick={() => setOpen((o) => !o)}>
        <AccordionTitle>{title}</AccordionTitle>
        <AccordionIcon
          animate={open ? "open" : "collapsed"}
          variants={iconVariants}
          transition={{ duration: 0.3 }}
        >
          <ChevronDownIcon />
        </AccordionIcon>
      </AccordionHeader>
      <AccordionContent
        animate={open ? "open" : "collapsed"}
        variants={variants}
        transition={{ duration: 0.3 }}
      >
        <BoxesRow>
          {responsibilities1.length > 0 && <ResponsibilitiesGroup data={responsibilities1} />}
          {responsibilities2.length > 0 && <ResponsibilitiesGroup data={responsibilities2} />}
        </BoxesRow>
        <DescriptionBox>{description}</DescriptionBox>
      </AccordionContent>
    </AccordionContainer>
  );
}

// Main Page
export default function DepartmentPage() {
  return (
    <PageWrapper>
      {/* Main Department */}
      <MainDeptWrapper>
        <MainDeptTitle>تعلیمی نصاب کمیٹی (جامعہ محمدیہ عربیہ)</MainDeptTitle>
        <MainDeptResponsibles>
          سرپرست: <span>شیخ ابوحمدان اشرف فیضی حفطہ اللہ</span> |{" "}
          <span>شیخ عبدالعظیم جامعی حفطہ اللہ</span> |{" "}
          <span>شیخ عبدالباری جامعی مدنی حفطہ اللہ</span>
        </MainDeptResponsibles>
      </MainDeptWrapper>

      {/* Accordion Items */}
     <SubDepartmentAccordion
  title="نصاب تعلیمی کمیٹی"
  responsibilities1={[
    { role: "سرپرست" , name: "شیخ ابوحمدان اشرف فیضی حفطہ اللہ" },
    { role: "سرپرست", name: "شیخ عبدالعظیم جامعی حفطہ اللہ" },
    { role: "سرپرست", name: "شیخ عبدالوکیل محمدی مدنی حفطہ اللہ" },
    { role: "سرپرست", name: "شیخ عبدالحسیب عمری مدنی حفطہ اللہ" }
  ]}
  responsibilities2={[
    { role: "معاون", name: "شیخ عبدالباری جامعی مدنی حفطہ اللہ" },
    { role: "معاون", name: "شیخ حافظ محمد امین عمری مدنی حفطہ اللہ" },
    { role: "معاون", name: "شیخ وسیم قاضی جامعی مدنی حفطہ اللہ" },
    { role: "معاون", name: "شیخ عبدالرحمن محمدی مدنی حفطہ اللہ" }
  ]}
  description={
    <>
      <p>
        کسی بھی تعلیمی ادارے کی کامیابی اور استحکام کا دارومدار اس کے نصاب تعلیم پر ہوتا ہے۔ 
        ایک معیاری اور منظم نصاب تعلیم نہ صرف ادارے کے تعلیمی معیار کو بلند کرتا ہے بلکہ طلبہ کی علمی، 
        فکری، اور عملی تربیت میں بھی کلیدی کردار ادا کرتا ہے۔ اس اہم ضرورت کے پیش نظر، جامعہ محمدیہ عربیہ نے 
        ایک مستقل نصاب تعلیمی کمیٹی قائم کی ہے، جو ادارے کے نصاب کو منظم، متوازن، اور معیاری بنانے کی ذمہ دار ہے۔
      </p>
      <br />
      <p>
        کمیٹی میں وہ اساتذہ اور ماہرین شامل کیے گئے ہیں جو دینی علوم اور اپنے فن میں مہارت رکھتے ہیں۔ 
        اراکین کا انتخاب اس بات کو مدنظر رکھتے ہوئے کیا گیا ہے کہ وہ مختلف جامعات و مدارس کے نصاب تعلیم سے واقف ہوں 
        اور جدید تقاضوں کو سمجھتے ہوں۔ اس کمیٹی کے اراکین وقتاً فوقتاً ملک کی معتبر علمی شخصیات سے مشورے لیتے ہیں 
        تاکہ نصاب میں موجود کسی بھی کمی کو دور کیا جا سکے اور اسے مزید بہتر بنایا جا سکے۔
      </p>
      <br />
      <p>
        نصاب تعلیمی کمیٹی جامعہ محمدیہ عربیہ کے تعلیمی معیار کو بلند کرنے میں اہم ستون کی حیثیت رکھتی ہے۔ 
        اس کی کاوشیں طلبہ کی تعلیمی و عملی تربیت اور ادارے کی مجموعی ترقی میں نمایاں کردار ادا کر رہی ہیں۔ 
        ان کی محنت کا ثمرہ یہ ہے کہ جامعہ کے فارغ التحصیل طلبہ نہ صرف دینی علوم کے ماہر ہوتے ہیں 
        بلکہ مختلف فنون میں بھی نمایاں مقام حاصل کر رہے ہیں۔
      </p>
    </>
  }
/>


     <SubDepartmentAccordion
  title="شعبہ تحفیظ قرآن"
  responsibilities1={[
    { role: "مشرف عمومی", name: "شیخ حافظ محمد امین عمری مدنی حفطہ اللہ" },
    { role: "مدیر", name: "شیخ حافظ شاکر احمد عمری حفطہ اللہ" },
    { role: "نائب مدیر", name: "شیخ حافظ محمد رفیق جامعی حفطہ اللہ" },
  ]}
  responsibilities2={[
    { role: "کلاسز", name: "ناظرہ، حفظ الف، حفظ ب، حفظ جیم، حفظ دال" },
  ]}
  description={
    <>
      جامعہ محمدیہ عربیہ کے مختلف علمی و دینی شعبوں میں سے شعبۂ حفظ ایک نہایت اہم
      اور ممتاز شعبہ ہے، جس کا مقصد قرآن مجید کو حفظ کرانے اور طلبہ کو دینی تعلیمات سے
      روشناس کرانا ہے۔ اس شعبے کا قیام جامعہ کے روزِ اوّل سے ہی عمل میں آیا تاکہ کم سن
      طلبہ کو ایسے علمی ماحول میں قرآن مجید حفظ کرنے کا موقع فراہم کیا جا سکے۔ الحمدللہ،
      اس شعبے سے اب تک سینکڑوں طلبہ قرآن مجید کو مکمل حفظ اور تجوید کے ساتھ یاد کر
      چکے ہیں اور ملتِ اسلامیہ کی مختلف میدانوں میں خدمت انجام دے رہے ہیں۔
      <br /><br />
      <strong>خصوصیات:</strong>
      <ul>
        <li>ہر طالب علم پر خصوصی توجہ اور انفرادی نگرانی۔</li>
        <li>طلبہ کی روزانہ اور ماہانہ ڈائری کا نظام۔</li>
        <li>والدین سے مسلسل رابطہ اور ملاقات کا اہتمام۔</li>
        <li>اساتذہ کی زیر نگرانی تعلیمی و اخلاقی تربیت۔</li>
        <li>مراجعہ کے لیے فجر تا 7 بجے اور مغرب تا عشاء پرسکون ماحول۔</li>
        <li>ادعیہ، املاء نویسی اور دینیات کا مرتب نصاب۔</li>
        <li>طلبہ کو تجوید و قراءت کے اصولوں پر عبور۔</li>
        <li>اخلاقی و روحانی تربیت کا خصوصی نظام۔</li>
      </ul>
      <br />
      <strong>شرائط و ہدایات برائے داخلہ:</strong>
      <ul>
        <li>داخلہ فارم مکمل اور صحیح معلومات کے ساتھ جمع کرانا۔</li>
        <li>عمر کی حد: ناظرہ کے لیے 7-9 سال، حفظ کے لیے زیادہ سے زیادہ 10 سال۔</li>
        <li>داخلے سے قبل امتحان اور ابتدائی 2 ماہ عارضی داخلہ۔</li>
        <li>80% حاضری اور وقت کی پابندی لازمی۔</li>
        <li>مقررہ یونیفارم اور صفائی کا اہتمام۔</li>
        <li>نصاب مکمل کیے بغیر سندِ فراغت نہیں دی جائے گی۔</li>
        <li>اساتذہ اور عملہ کے ادب و احترام کی پابندی۔</li>
        <li>والدین کی ذمہ داری: مدرسے کے ساتھ مسلسل رابطہ۔</li>
        <li>موبائل فون یا غیر ضروری اشیاء سختی سے منع۔</li>
        <li>طبی معائنہ سال میں کم از کم ایک مرتبہ۔</li>
        <li>طلبہ کے باہمی تعلقات میں حسنِ سلوک ضروری۔</li>
      </ul>
      <br />
      یہ تمام خصوصیات شعبۂ حفظ کو مزید کامیاب بناتی ہیں اور طلبہ کی دینی و علمی ترقی میں اہم کردار ادا کرتی ہیں۔
    </>
  }
/>

      <SubDepartmentAccordion
  title="شعبہ امتحانات"
  responsibilities1={[
    { role: "مدیر", name: "شیخ عبدالرحمن محمدی مدنی حفطہ اللہ" },
    { role: "نائب مدیر", name: "شیخ صلاح الدین الجامعی حفطہ اللہ" },
    { role: "معاون", name: "شیخ وسیم قاضی جامعی مدنی حفطہ اللہ" },
  ]}
  responsibilities2={[
    { role: "امتحانات", name: "سہ ماہی، ششماہی، سالانہ" },
  ]}
  description={
    <>
      طلبہ کی تعلیمی استعداد کو جانچنے اور معیار تعلیم کو مستحکم کرنے کے لیے جامعہ
      میں شعبۂ امتحانات قائم کیا گیا ہے۔ جامعہ میں تین امتحانات منعقد ہوتے ہیں:
      سہ ماہی (20 نمبر)، ششماہی (30 نمبر)، اور سالانہ (50 نمبر)۔ کامیابی کے لیے
      کم از کم 50 فیصد نمبر حاصل کرنا لازمی ہے۔ امتحانات تحریری صورت میں لیے جاتے ہیں،
      اور امتحان کمیٹی ان کی نگرانی و جانچ کرتی ہے۔
      <br /><br />
      <strong>امتحانات کے اصول و ضوابط:</strong>
      <ul>
        <li>کامیابی کے لیے 50 فیصد نمبر لازمی۔</li>
        <li>ضمنی امتحان صرف دو مضامین میں ناکام طلبہ کے لیے۔</li>
        <li>تین یا زیادہ مضامین میں ناکامی پر جامعہ میں گنجائش نہیں۔</li>
        <li>80% حاضری لازمی۔</li>
        <li>نقل یا خلاف ورزی پر سخت کارروائی۔</li>
      </ul>
      <br />
      <strong>امتحان ہال کے اصول:</strong>
      <ul>
        <li>سفید لباس لازمی۔</li>
        <li>جوابی کاپی پر مکمل معلومات درج کرنا۔</li>
        <li>امتحان کے دوران مکمل خاموشی۔</li>
        <li>موبائل یا الیکٹرانک ڈیوائس منع۔</li>
        <li>وقت کی سخت پابندی۔</li>
      </ul>
      <br />
      <strong>نگران کی ذمہ داریاں:</strong> طلبہ کی مکمل نگرانی، نقل روکنا،
      اضافی ورقے پر دستخط، وقت کی پابندی، اور رپورٹ تیار کرنا۔
      <br /><br />
      <strong>کامیابی کے درجات:</strong>
      <ul>
        <li>ممتاز (85% اور اس سے زیادہ)</li>
        <li>جید جداً (75-84%)</li>
        <li>جید (60-74%)</li>
        <li>مقبول (50-59%)</li>
        <li>راسب (50% سے کم یا کسی ایک مادہ میں ناکامی)</li>
      </ul>
    </>
  }
/>


      <SubDepartmentAccordion
  title="شعبہ عربی"
  responsibilities1={[
    { role: "نگران", name: "نام دستیاب نہیں" }
  ]}
  responsibilities2={[
    { role: "کلاس", name: "عربی زبان و ادب" }
  ]}
  description={
    <>
      <p>
        شعبہ عربی میں طلبہ کو عربی زبان اور ادب کی جامع تعلیم دی جاتی ہے۔ اس کا مقصد طلبہ کو دینی متون، قرآن و حدیث، سیرت، فقہ، اور دیگر اسلامی علوم کی اصل زبان میں فہم اور مہارت عطا کرنا ہے۔
      </p>
      <p>
        اس شعبے کے تحت عربی قواعد (نحو و صرف)، لغت، بلاغت، ادب عربی کی مقبول کتابیں، اور تحریری و تقریری مشق پر خاص زور دیا جاتا ہے۔ عربی زبان کی تدریس درجات کے اعتبار سے منظم ہے اور نصاب میں درج ذیل اہداف کو حاصل کیا جاتا ہے:
      </p>
      <ul>
        <li>طلبہ عربی متن کو روانی سے پڑھ اور سمجھ سکیں۔</li>
        <li>عربی میں خود مضمون نگاری، تقریر، اور محاوراتی گفتگو کی صلاحیت پیدا ہو۔</li>
        <li>دینی علوم (قرآن، حدیث، فقہ) کے اصل مآخذ تک بغیر ترجمہ کے رسائی ممکن ہو۔</li>
      </ul>
      <p>
        اس شعبے میں فصاحت و بلاغت کے اصول سیکھائے جاتے ہیں، تاکہ طلبہ عربی زبان میں مستقلاً تحقیق و تدریس کا اہل بن سکے۔ ہر سال جماعتوں کے اعتبار سے کتابیں بدلی جاتی ہیں تاکہ آخری سالوں تک عربی مطالعہ، شرح، بحث و تجزیہ میں مہارت آجائے۔ 
      </p>
      <p>
        اعلیٰ درجات میں کلاسیکل اور معاصر عربی ادب کی کتابیں، کلاسیکی لغت، اور تاریخِ ادبِ عربی وغیرہ بھی شامل نصاب ہیں۔
      </p>
      <p>
        شعبہ عربی کا ہدف یہ ہے کہ جامعہ کے طلبہ دینی و عصری مناہج میں عربیت و تحقیق کے لحاظ سے ممتاز ہوں اور عملی زندگی میں عربی زبان کو بطورِ ذریعہ علم و دعوت برت سکیں۔
      </p>
    </>
  }
/>


<SubDepartmentAccordion
  title="شعبہ اردو و انگریزی"
  responsibilities1={[{ role: "نگران", name: "نام دستیاب نہیں" }]}
  responsibilities2={[
    { role: "کلاس", name: "اردو" },
    { role: "کلاس", name: "انگریزی" }
  ]}
  description={
    <>
      <p>
        شعبہ اردو و انگریزی جامعہ کا ایک بنیادی شعبہ ہے۔ اس میں طلبہ کو اردو اور انگریزی زبان کی جامع و عملی تعلیم دی جاتی ہے  
        تاکہ وہ ابلاغ، تحریر، تحقیق اور عملی زندگی ہر میدان میں زبان پر عبور حاصل کریں۔
      </p>
      <strong>شعبہ اردو کی خصوصیات:</strong>
      <ul>
        <li>اردو ادب، قواعد، مضمون نویسی، تقریر، اسلامی اور اصلاحی تحریروں کی تربیت۔</li>
        <li>صحافتی ذوق پیدا کرنا (وال میگزین، سالنامہ، ادبی مقابلے وغیرہ)۔</li>
        <li>اردو دینی کتب کی فہم اور ادبی اظہار کی پختہ بنیادیں۔</li>
      </ul>
      <strong>شعبہ انگریزی کی خصوصیات:</strong>
      <ul>
        <li>انگریزی زبان کے بنیادی اور میانہ درجے کے گرامر اصول، ہماری نصاب میں شامل ہیں۔</li>
        <li>انگریزی مکالمہ و ترجمہ (Conversation/Translation) عملی طور پر سیکھایا جاتا ہے۔</li>
        <li>انگریزی میں تحریری (Essay/Letter) اور تقاریر کی تربیت دی جاتی ہے۔</li>
        <li>جدید تعلیم و تحقیق کے امکانات سے آگاہی دینے کے لیے موجودہ نصاب میں عصری موضوعات کا تعارف بھی شامل ہے۔</li>
      </ul>
      <p>
        اس شعبہ کا مقصد طلبہ کا فکری دائرہ وسیع کرنا ہے تاکہ وہ دینی خدمات، تحقیق، دعوت اور عصری میدانوں میں زبان کی بنیاد پر پُر اعتماد رہیں۔  
        عملی مقاصد کے لئے املا نویسی (Dictation)، مباحثے (Debates)، اور گفتگو (Oral Practice) کا بھرپور نظام ہے۔
      </p>
      <p>
        اس کے ذریعے طلبہ مقابلہ جاتی امتحانات، تدریسی و دفتری عملی زندگی، اور تعلیمی ذرائع ابلاغ میں قابل اور فعال کردار ادا کر سکتے ہیں۔
      </p>
    </>
  }
/>

      
    </PageWrapper>
  );
}
